import React, { useState } from "react";
import { MdArrowBack } from "react-icons/md";
import { BiInfoCircle } from "react-icons/bi";
import { AiOutlineIdcard } from "react-icons/ai";
import { IoPersonAddOutline } from "react-icons/io5";
import { MdOutlineBackspace } from "react-icons/md";
import { Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import useDesktopSize from "../../../../../../utils/hooks/useDesktopSize";
import useCourtPasses from "../../../hooks/useCourtPasses";
import EAButton from "../../../../shared/Buttons/EAButton";
import EAAlert from "../../../../shared/EAAlert";
import EADivider from "../../../../shared/EADivider";
import EAIconButton from "../../../../shared/Buttons/EAIconButton";
import EAModal from "../../../../shared/Modal/EAModal";
import EASkeletonLoader from "../../../../shared/EASkeletonLoader";
import { Exercise } from "../../../../../models/Exercise";
import { Ticket } from "../../../../../models/Ticket";
import "./PassSelection.scss";
import getId from "../../../../../../utils/getId";

const HtmlTooltip = styled(({ className, ...props }: any) => <Tooltip {...props} classes={{ popper: className }} />)(
  ({ theme }) => ({
    [`& .MuiTooltip-tooltip`]: {
      backgroundColor: "#f5f5f9",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 300,
      fontSize: theme.typography.pxToRem(12),
      border: "1px solid #dadde9",
    },
  })
);

interface IProps {
  exerciseDetails: Exercise | null;
  onPassSelected: (passId: string, assigned?: boolean) => void;
  onBack: () => void;
  alert?: string;
  loading?: boolean;
}

const PassSelection: React.FC<IProps> = ({
  exerciseDetails,
  onPassSelected,
  onBack,
  alert,
  loading: passLoading = false,
}) => {
  const { t } = useTranslation();
  const isDesktop = useDesktopSize();
  const { loading, getFilteredPasses } = useCourtPasses();
  const [selectedPassInfo, setSelectedPassInfo] = useState<Ticket | null>(null);

  if (!exerciseDetails) {
    return <EASkeletonLoader height="200px" />;
  }

  const availablePasses = getFilteredPasses(exerciseDetails.startTime);

  const formatTimeRange = (startTime: number, endTime: number) => {
    const startFormatted = format(startTime, "HH:mm");
    const endFormatted = format(endTime, "HH:mm");
    return `${startFormatted} - ${endFormatted}`;
  };

  return (
    <div className="pass-selection">
      <div className="pass-selection__summary">
        <h5>{exerciseDetails.exerciseGroup.displayName}</h5>
        <p>{formatTimeRange(exerciseDetails.startTime, exerciseDetails.closeTime)}</p>
      </div>

      {alert && <EAAlert type="error" message={alert} />}

      {loading ? (
        <EASkeletonLoader height="150px" />
      ) : availablePasses.length > 0 ? (
        <>
          <EADivider />
          <div className="pass-selection__list">
            {availablePasses.map((pass, index) => (
              <div key={index} className="pass-card">
                <div className="pass-card__header">
                  <div className="pass-card__info">
                    <div className="pass-card__title">
                      <AiOutlineIdcard className="pass-card__icon" />
                      <span>{pass.name}</span>
                      {pass.description.length > 2 && (
                        <>
                          {isDesktop ? (
                            <HtmlTooltip
                              placement="right"
                              arrow={true}
                              title={
                                <div className="tooltip-description">
                                  <p>
                                    <strong>Nazwa karnetu:</strong> {pass.name}
                                  </p>
                                  <p>
                                    <strong>Pozostała ilość wejść:</strong> {pass.entrances - pass.exercises.length}
                                  </p>
                                  <p>
                                    <strong>Karnet ważny:</strong> {format(pass.validfrom, "dd.MM")} -{" "}
                                    {format(pass.validto, "dd.MM.yyyy")}
                                  </p>
                                </div>
                              }
                            >
                              <BiInfoCircle className="pass-card__tooltip-icon" />
                            </HtmlTooltip>
                          ) : (
                            <EAIconButton
                              title=""
                              icon={<BiInfoCircle />}
                              variant="icon"
                              onClick={() =>
                                setSelectedPassInfo(selectedPassInfo?.objectId === pass.objectId ? null : pass)
                              }
                            />
                          )}
                        </>
                      )}
                    </div>
                    <div className="pass-card__details">
                      <span>
                        Pozostało: <strong>{pass.entrances - pass.exercises.length}</strong> wejść
                      </span>
                      <span>
                        Ważny do: <strong>{format(pass.validto, "dd.MM.yyyy")}</strong>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pass-card__action">
                  {exerciseDetails.assigned ? (
                    <HtmlTooltip placement="top" title="Kliknij, aby wypisać się z tej rezerwacji.">
                      <div>
                        <EAButton
                          title="Wypisz się"
                          onClick={() => onPassSelected(getId(pass), true)}
                          startIcon={<MdOutlineBackspace />}
                          variant="outlined"
                          color="secondary"
                          disabled={passLoading}
                        />
                      </div>
                    </HtmlTooltip>
                  ) : (
                    <EAButton
                      title="Zapisz się tym karnetem"
                      onClick={() => onPassSelected(getId(pass), false)}
                      startIcon={<IoPersonAddOutline />}
                      variant="outlined"
                      disabled={passLoading}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <EAAlert type="warning" message="Nie masz dostępnych karnetów do zapisania się na te zajęcia." />
      )}

      <div className="pass-selection__footer">
        <EAButton title="Powrót" onClick={onBack} startIcon={<MdArrowBack />} variant="text" fullWidth />
      </div>

      {/* Modal z informacjami o karnecie dla mobile */}
      {selectedPassInfo && (
        <EAModal
          isOpen={!!selectedPassInfo}
          onClose={() => setSelectedPassInfo(null)}
          title={selectedPassInfo.name}
          hideButtons
        >
          {selectedPassInfo.description.length > 1 ? (
            <div dangerouslySetInnerHTML={{ __html: selectedPassInfo.description }} />
          ) : (
            <p>{t("brak_opisu")}</p>
          )}
        </EAModal>
      )}
    </div>
  );
};

export default PassSelection;
